import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Coins, Package, Plus, Edit2, Trash2, Save, X, Check, Building2,
  TrendingUp, Eye, Star, Zap, RefreshCw, Settings, Monitor, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UltraCredits() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('packages');
  const [packages, setPackages] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const [editPkg, setEditPkg] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newPkg, setNewPkg] = useState({ name: '', credits: 100, price_usd: 10.0, badge: '', is_popular: false, savings_pct: 0, is_active: true });
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustNote, setAdjustNote] = useState('');
  const [settingsEdit, setSettingsEdit] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pkgRes, walletRes, showRes, settRes] = await Promise.all([
        axios.get(`${API}/api/ultra/credits/packages`, { headers }),
        axios.get(`${API}/api/ultra/credits/wallets`, { headers }),
        axios.get(`${API}/api/ultra/credits/showcase`, { headers }),
        axios.get(`${API}/api/ultra/credits/settings`, { headers }),
      ]);
      setPackages(pkgRes.data.packages || []);
      setWallets(walletRes.data.wallets || []);
      setShowcase(showRes.data.items || []);
      setSettings(settRes.data.settings || {});
      setSettingsDraft(settRes.data.settings || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDeletePkg = async (id) => {
    if (!window.confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;
    await axios.delete(`${API}/api/ultra/credits/packages/${id}`, { headers });
    setPackages(p => p.filter(x => x.id !== id));
  };

  const handleSavePkg = async (pkg) => {
    await axios.patch(`${API}/api/ultra/credits/packages/${pkg.id}`, pkg, { headers });
    setPackages(p => p.map(x => x.id === pkg.id ? pkg : x));
    setEditPkg(null);
  };

  const handleAddPkg = async () => {
    const res = await axios.post(`${API}/api/ultra/credits/packages`, newPkg, { headers });
    setPackages(p => [...p, res.data.package]);
    setShowAdd(false);
    setNewPkg({ name: '', credits: 100, price_usd: 10.0, badge: '', is_popular: false, savings_pct: 0, is_active: true });
  };

  const handleAdjust = async () => {
    try {
      const res = await axios.post(`${API}/api/ultra/credits/adjust`, {
        org_id: adjustModal.org_id,
        amount: adjustAmount,
        description: adjustNote || 'Admin tarafından manuel düzenleme',
      }, { headers });
      alert(res.data.message);
      await fetchAll();
      setAdjustModal(null);
      setAdjustAmount(0);
      setAdjustNote('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleSaveSettings = async () => {
    await axios.patch(`${API}/api/ultra/credits/settings`, settingsDraft, { headers });
    setSettings(settingsDraft);
    setSettingsEdit(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Coins className="w-7 h-7 text-amber-500" />
            Kredi Yönetimi
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Kredi paketleri, cüzdanlar ve vitrin yönetimi</p>
        </div>
        <button onClick={fetchAll} className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-600 transition-colors">
          <RefreshCw className="w-4 h-4" />Yenile
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Aktif Paket', value: packages.filter(p => p.is_active).length, icon: Package, color: 'text-blue-600 bg-blue-50' },
          { label: 'Toplam Cüzdan', value: wallets.length, icon: Coins, color: 'text-amber-600 bg-amber-50' },
          { label: 'Vitrin Öğesi', value: showcase.filter(s => s.status === 'active').length, icon: Monitor, color: 'text-green-600 bg-green-50' },
          { label: 'Toplam Bakiye', value: wallets.reduce((s, w) => s + (w.balance || 0), 0).toLocaleString() + ' kr', icon: TrendingUp, color: 'text-purple-600 bg-purple-50' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <div className={`w-9 h-9 rounded-lg ${kpi.color} flex items-center justify-center mb-2`}>
              <kpi.icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            <p className="text-xs text-gray-500">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { id: 'packages', label: 'Paketler', icon: Package },
          { id: 'wallets', label: 'Cüzdanlar', icon: Coins },
          { id: 'showcase', label: 'Vitrin', icon: Monitor },
          { id: 'settings', label: 'Ayarlar', icon: Settings },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab: Packages */}
      {tab === 'packages' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />Yeni Paket Ekle
            </button>
          </div>

          {showAdd && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
              <h3 className="font-semibold text-gray-900">Yeni Kredi Paketi</h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { key: 'name', label: 'Paket Adı', type: 'text', placeholder: 'Başlangıç Paketi' },
                  { key: 'credits', label: 'Kredi Miktarı', type: 'number', placeholder: '100' },
                  { key: 'price_usd', label: 'Fiyat ($)', type: 'number', placeholder: '10' },
                  { key: 'badge', label: 'Rozet', type: 'text', placeholder: 'Popüler' },
                  { key: 'savings_pct', label: 'Tasarruf %', type: 'number', placeholder: '0' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs text-gray-600 mb-1">{f.label}</label>
                    <input
                      type={f.type}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder={f.placeholder}
                      value={newPkg[f.key]}
                      onChange={e => setNewPkg(p => ({ ...p, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newPkg.is_popular} onChange={e => setNewPkg(p => ({ ...p, is_popular: e.target.checked }))} className="accent-amber-500" />
                  Popüler
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newPkg.is_active} onChange={e => setNewPkg(p => ({ ...p, is_active: e.target.checked }))} className="accent-amber-500" />
                  Aktif
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={handleAddPkg} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium">
                  <Save className="w-4 h-4" />Kaydet
                </button>
                <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  İptal
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {packages.map(pkg => (
              <div key={pkg.id} className={`bg-white border rounded-xl p-5 ${pkg.is_popular ? 'border-amber-300' : 'border-gray-100'}`}>
                {editPkg?.id === pkg.id ? (
                  <div className="space-y-3">
                    {[
                      { key: 'name', label: 'Paket Adı', type: 'text' },
                      { key: 'credits', label: 'Kredi', type: 'number' },
                      { key: 'price_usd', label: 'Fiyat ($)', type: 'number' },
                      { key: 'badge', label: 'Rozet', type: 'text' },
                      { key: 'savings_pct', label: 'Tasarruf %', type: 'number' },
                    ].map(f => (
                      <div key={f.key} className="flex items-center gap-3">
                        <label className="w-28 text-xs text-gray-500 shrink-0">{f.label}</label>
                        <input
                          type={f.type}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                          value={editPkg[f.key]}
                          onChange={e => setEditPkg(p => ({ ...p, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                        />
                      </div>
                    ))}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={editPkg.is_popular} onChange={e => setEditPkg(p => ({ ...p, is_popular: e.target.checked }))} className="accent-amber-500" />Popüler
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={editPkg.is_active} onChange={e => setEditPkg(p => ({ ...p, is_active: e.target.checked }))} className="accent-amber-500" />Aktif
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleSavePkg(editPkg)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"><Save className="w-3.5 h-3.5" />Kaydet</button>
                      <button onClick={() => setEditPkg(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200"><X className="w-3.5 h-3.5" />İptal</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900">{pkg.name}</p>
                        {pkg.badge && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">{pkg.badge}</span>}
                        {!pkg.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Pasif</span>}
                      </div>
                      <p className="text-2xl font-black text-gray-900 mt-1">{pkg.credits.toLocaleString()} <span className="text-sm font-normal text-gray-500">kredi</span></p>
                      <p className="text-lg font-bold text-amber-600">${pkg.price_usd}</p>
                      {pkg.savings_pct > 0 && <p className="text-xs text-green-600">%{pkg.savings_pct} tasarruf</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditPkg({ ...pkg })} className="p-2 hover:bg-amber-50 rounded-lg text-amber-600 transition-colors"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDeletePkg(pkg.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Wallets */}
      {tab === 'wallets' && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Organizasyon', 'Tür', 'Bakiye', 'Satın Alınan', 'Harcanan', 'İşlem'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {wallets.map(w => (
                <tr key={w.org_id} className="hover:bg-gray-50/50">
                  <td className="px-5 py-4 font-medium text-gray-900">{w.org_name || w.org_id}</td>
                  <td className="px-5 py-4 text-gray-500">{w.org_type || '-'}</td>
                  <td className="px-5 py-4">
                    <span className={`font-bold ${w.balance > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {(w.balance || 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-green-600">{(w.total_purchased || 0).toLocaleString()}</td>
                  <td className="px-5 py-4 text-red-400">{(w.total_spent || 0).toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => { setAdjustModal(w); setAdjustAmount(0); setAdjustNote(''); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-medium transition-colors"
                    >
                      <Zap className="w-3.5 h-3.5" />Düzenle
                    </button>
                  </td>
                </tr>
              ))}
              {wallets.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">Henüz cüzdan yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: Showcase */}
      {tab === 'showcase' && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Proje', 'Firma', 'Durum', 'Başlangıç', 'Bitiş', 'Kredi'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {showcase.map(item => (
                <tr key={item.id} className="hover:bg-gray-50/50">
                  <td className="px-5 py-4 font-medium text-gray-900 max-w-[200px] truncate">{item.project_title}</td>
                  <td className="px-5 py-4 text-gray-600">{item.org_name}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      item.status === 'active' ? 'bg-green-100 text-green-700' :
                      item.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                      'bg-red-100 text-red-600'
                    }`}>{item.status === 'active' ? 'Aktif' : item.status === 'expired' ? 'Sona Erdi' : 'İptal'}</span>
                  </td>
                  <td className="px-5 py-4 text-gray-500">{item.start_date ? new Date(item.start_date).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="px-5 py-4 text-gray-500">{item.end_date ? new Date(item.end_date).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="px-5 py-4 text-amber-600 font-medium">{item.credits_spent}</td>
                </tr>
              ))}
              {showcase.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">Henüz vitrin öğesi yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: Settings */}
      {tab === 'settings' && (
        <div className="bg-white border border-gray-100 rounded-xl p-6 space-y-5 max-w-lg shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Genel Kredi Ayarları</h3>
            {!settingsEdit ? (
              <button onClick={() => setSettingsEdit(true)} className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-sm transition-colors"><Edit2 className="w-4 h-4" />Düzenle</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSaveSettings} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"><Save className="w-4 h-4" />Kaydet</button>
                <button onClick={() => { setSettingsEdit(false); setSettingsDraft(settings); }} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>
          {[
            { key: 'showcase_cost', label: 'Vitrin Maliyeti (kredi)', desc: '30 günlük vitrin fiyatı' },
            { key: 'showcase_duration_days', label: 'Vitrin Süresi (gün)', desc: 'Kaç gün vitrinvde kalacak' },
            { key: 'credits_per_usd', label: 'Kredi / Dolar Oranı', desc: '1 dolara kaç kredi' },
          ].map(f => (
            <div key={f.key} className="flex items-start gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">{f.label}</label>
                <p className="text-xs text-gray-400">{f.desc}</p>
              </div>
              {settingsEdit ? (
                <input
                  type="number"
                  className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 text-right"
                  value={settingsDraft[f.key] || 0}
                  onChange={e => setSettingsDraft(s => ({ ...s, [f.key]: parseFloat(e.target.value) || 0 }))}
                />
              ) : (
                <span className="font-bold text-amber-600 text-lg">{settings[f.key] ?? '—'}</span>
              )}
            </div>
          ))}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">Ayarlar kaydedildikten sonra yeni satın alımlara uygulanır. Mevcut aktif vitriner etkilenmez.</p>
          </div>
        </div>
      )}

      {/* Adjust Credits Modal */}
      {adjustModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Kredi Düzenle</h3>
              <button onClick={() => setAdjustModal(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600">Firma: <strong>{adjustModal.org_name}</strong></p>
            <p className="text-sm text-gray-600">Mevcut Bakiye: <strong className="text-amber-600">{adjustModal.balance} kredi</strong></p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eklenecek/Çıkarılacak Kredi</label>
              <p className="text-xs text-gray-400 mb-2">Kredi eklemek için pozitif, çıkarmak için negatif girin</p>
              <input
                type="number"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                value={adjustAmount}
                onChange={e => setAdjustAmount(parseInt(e.target.value) || 0)}
                placeholder="Örn: 100 veya -50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                value={adjustNote}
                onChange={e => setAdjustNote(e.target.value)}
                placeholder="Kampanya kredisi, hata düzeltme vb."
              />
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-sm text-gray-600">Yeni Bakiye: <strong className="text-amber-600">{(adjustModal.balance + adjustAmount).toLocaleString()} kredi</strong></p>
            </div>
            <button
              onClick={handleAdjust}
              disabled={adjustAmount === 0}
              className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Uygula
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
